import { createContext, useEffect, useState } from 'react';

import { GetParams, useFetch } from '@uservoice/rest-utils';

import { Account, Subdomain, User, getCurrentAccount, CurrentAccountResponse } from '@/core/api/collections/currentAccount';
import auth from '@/core/auth/AuthService';

export type LicensesRemaining = {
  admin: number;
  support: number;
  contributor: number;
}
type AuthCtx = {
  loading: boolean;
  currentUser?: User;
  currentSubdomain?: Subdomain;
  currentAccount?: Account;
  hasCoreAccess?: boolean;
  hasUvkAccess?: boolean;
  licensesRemaining?: LicensesRemaining;
  hasAccessTo: (flag: string) => boolean;
  logout: () => void;
  afterLogout: (name: string, callback: () => void) => void;
  updateBillingRate: (rate: number) => void;
}

export const AuthContext = createContext<AuthCtx>({
  loading: true,
  hasAccessTo: () => false,
  logout: () => {},
  afterLogout: () => {},
  updateBillingRate: () => {},
});

const afterLogoutCallbacks: { [key: string]: () => void } = {};
export function AuthProvider ({ children }: { children: React.ReactNode }) {
  const [initializing, setInitializing] = useState(true);
  const [currentUser, setCurrentUser] = useState<User | undefined>(undefined);
  const [currentSubdomain, setCurrentSubdomain] = useState<Subdomain | undefined>();
  const [currentAccount, setCurrentAccount] = useState<Account | undefined>();
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [data, loading, _, fetchAccount] = useFetch<GetParams, CurrentAccountResponse>(getCurrentAccount, {}, { lazyFetch: true });

  const init = async () => {
    // If we're logged in, then our login callback will fetch the user's specific account.
    // If we're not signed in yet, go ahead and fetch the subdomain at least:
    if (!await auth.checkForSession()) {
      fetchAccount();
    }
  };

  const handleLogout = () => {
    setCurrentUser(undefined);
    Object.values(afterLogoutCallbacks).forEach(cb => cb());
  };

  // TODO: What if the fetchAccount request fails?
  useEffect(() => {
    if (data) {
      setCurrentUser(data.currentUser);
      setCurrentSubdomain(data.currentSubdomain);
      setCurrentAccount(data.currentAccount);
      setInitializing(false);
    }
  }, [data]);

  useEffect(() => {
    init();
    auth.appLoginCallback(() => fetchAccount());
    auth.appLogoutCallback(handleLogout);
  }, []);

  const enabledFlags = data?.authorizedFeatures || [];
  const hasAccessTo = (flag: string) => {
    return enabledFlags.indexOf(flag) > -1;
  };

  const logout = () => {
    if (currentUser) {
      auth.signOut();
    }
  };

  const afterLogout = (name: string, callback: () => void) => {
    afterLogoutCallbacks[name] = callback;
  };

  const updateBillingRate = (rate: number) => {
    if (currentAccount) {
      setCurrentAccount({ ...currentAccount, rate });
    }
  };

  return (
    <AuthContext.Provider value={{
      loading: initializing || loading,
      currentUser,
      currentSubdomain,
      currentAccount,
      hasCoreAccess: currentAccount?.coreAccess,
      hasUvkAccess: currentAccount?.uvkAccess,
      licensesRemaining: currentAccount ? {
        admin: (typeof currentAccount.maxFullaccessAdmins === 'undefined' || currentAccount.maxFullaccessAdmins === null)
          ? Number.MAX_SAFE_INTEGER
          : (currentAccount.maxFullaccessAdmins - currentAccount.currentFullaccessAdmins),
        support: (typeof currentAccount.maxHelpdeskAdmins === 'undefined' || currentAccount.maxHelpdeskAdmins === null)
          ? Number.MAX_SAFE_INTEGER
          : (currentAccount.maxHelpdeskAdmins - currentAccount.currentHelpdeskAdmins),
        contributor: (typeof currentAccount.maxContributors === 'undefined' || currentAccount.maxContributors === null)
          ? Number.MAX_SAFE_INTEGER
          : (currentAccount.maxContributors - currentAccount.currentContributors),
      } : undefined,
      hasAccessTo,
      logout,
      afterLogout,
      updateBillingRate,
    }}>
      {children}
    </AuthContext.Provider>
  );
}
